import { useCallback } from "react";
import { useInit } from "@follosoft/react-components";

import type { ContractContactAlertType } from "../types";

const ALERT_TYPE_GROUP = "CONTRACT_CONTACT_ALERT_TYPES";

/**
 * Resolves a `contract-contacts` alert type string to the constant id the write
 * endpoints expect. The group is public, so it comes from `/init` rather than
 * being hardcoded — an environment whose seeder hasn't run yields `undefined`,
 * which the caller sends as an omitted attribute instead of a wrong id.
 *
 * Statuses need no counterpart: rows come back with their status already
 * resolved to a string, and the frontend never writes one.
 */
export const useContractContactConstants = () => {
  const { constantsByGroup } = useInit();

  const idOf = useCallback(
    (group: string, value: string) => {
      const match = constantsByGroup(group).find((constant) => constant.attributes.value === value);

      return match ? Number(match.id) : undefined;
    },
    [constantsByGroup],
  );

  const alertTypeId = useCallback((alertType: ContractContactAlertType) => idOf(ALERT_TYPE_GROUP, alertType), [idOf]);

  /**
   * The id, or a thrown error naming why there isn't one. A write that omits
   * `alert_type_id` is rejected by the backend with nothing the form can pin to
   * a field, so it surfaces as a bare "failed to save" — this says which
   * constant is missing instead, which is a `/init` problem, not a form one.
   */
  const requireAlertTypeId = useCallback(
    (alertType: ContractContactAlertType) => {
      const id = alertTypeId(alertType);

      if (id === undefined) {
        throw new Error(`Missing ${ALERT_TYPE_GROUP} constants from /init — cannot resolve the "${alertType}" alert type.`);
      }

      return id;
    },
    [alertTypeId],
  );

  return { requireAlertTypeId };
};
